import { Host, inject, Injectable, OnDestroy } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { NavigationStart, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { filter, Subject, Subscription } from "rxjs";
import menuCoordinator from "../../assets/organisation/menu/coordinator.menu.json";
import menuCoach from "../../assets/shared/menu/coach.menu.json";
import menuGuest from "../../assets/shared/menu/guest.menu.json";
import menuResident from "../../assets/shared/menu/resident.menu.json";
import { ENVIRONMENT } from "../../environments/environment";
import { RoleName } from "../classes/flow/session/Session";
import Logger from "../classes/Logger";
import { MenuComponent, MenuItem } from "../components/menu/menu";
import { getLanguage } from "../helpers/determineLanguage";
import { AuthService } from "./auth.service";

@Injectable({
  providedIn: "root",
})
export class ApplicationService implements OnDestroy {
  private readonly titleService = inject(Title);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);

  private static readonly NAME: string = ENVIRONMENT.ORGANISATION;
  private menuMap: Map<RoleName, MenuItem[]> = new Map();

  // No suitable injection parameters
  public host?: Host;
  public subscriptions = new Map<string, Subscription[]>();
  public debug = !ENVIRONMENT.PRODUCTION;
  public initialized = false;
  public auth = inject(AuthService);

  // Loading related
  public loading = false;
  private totalLoading = 0;
  public loadingSubject: Subject<boolean> = new Subject();

  public blocked = false;
  public municipalityHomeURL: MenuItem = {
    href: ENVIRONMENT.SOCIALS.LINKS.MAIN,
    route: "",
    label: "MUNICIPALITY",
    icon: "open_in_new",
    index: 0,
  };
  public menu: MenuItem[] = [];
  public menuComponent!: MenuComponent;
  public municipalitySlogan = ENVIRONMENT.SOCIALS.SLOGAN;

  public constructor() {
    this.setLoading(true);

    this.subscribe(
      "routeChanges",
      this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
        this.blocked = false;
      }),
    );
  }

  /**
   * Starts the initialization of the application
   */
  public async initialize() {
    if (!this.initialized) {
      this.configureLanguage();
      this.finishInitialisation();
    }
  }

  private configureLanguage() {
    const language = getLanguage() ?? "";
    this.titleService.setTitle(ApplicationService.NAME);
    this.translateService.use(language);

    const html = document.getElementsByTagName("html")[0];
    html.setAttribute("lang", language);
  }

  private finishInitialisation() {
    this.host = "Web";
    this.initialized = true;
  }

  /**
   * Updates the title of the application
   * @param title The title
   */
  public updateTitle(title: string) {
    this.titleService.setTitle(`${ApplicationService.NAME} - ${title}`);
  }

  /**
   * Adds a subscription to be collected for auto-unsubscribe
   * @param key subscription key id
   * @param subscription one or multiple Subscriptions
   */
  public subscribe(key: string, ...subscription: Subscription[]) {
    try {
      this.subscriptions.set(key, [...(this.subscriptions.get(key) || []), ...subscription]);
    } catch (error) {
      Logger.error(`Unable to subscribe with key -> ${key}`);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Removes all subscriptions depending on key
   * @param key subscription key id
   */
  public unsubscribe(key: string) {
    if (this.debug) console.log(`DEBUG MODE: ${this.debug} [Component] Destroying id -> ${key}`);

    try {
      const subscriptions = this.subscriptions.get(key) || [];
      for (const subscription of subscriptions) subscription.unsubscribe();
    } catch (error) {
      Logger.error(`Unable to unsubscribe with key -> ${key}`);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * @param error The error given
   * @returns The error as a string in case it is not
   */
  public getErrorMessage(error: unknown) {
    if (error instanceof Error) return error.message;
    return String(error);
  }

  /**
   * Initializes the menu
   */
  public initMenu() {
    this.menuMap = new Map([
      ["coordinator", menuCoordinator.menu.items],
      ["coach", menuCoach.menu.items],
      ["resident", menuResident.menu.items],
    ]);
    this.menu = [...menuGuest.menu.items, this.municipalityHomeURL];

    // if (this.session.user?.isDeleted()) {
    //   if (!this.menuDeletedUser) return;
    //   if (!this._menu) return;
    //   this.menu = this.menuDeletedUser.menu.items.concat(...this._menu.menu.items);
    // } else {
    let menu: MenuItem[] = this.menuMap.get(this.auth.roleName) ?? [];
    console.log(this.menuMap.get(this.auth.roleName));

    if (this.auth.roleName === "coordinator" && this.auth.specialties.some((spe) => spe === 11)) {
      if (!this.auth.specialties.some((spe) => spe === 12)) {
        menu = menu.filter((item) => item.label !== "DASHBOARD_STATISTICS_COORDINATOR");
      }

      if (!this.auth.specialties.some((spe) => spe === 13)) {
        menu = menu.filter(
          (item) =>
            item.label !== "QUESTIONNAIRES" &&
            item.label !== "DASHBOARD_COORDINATOR" &&
            item.label !== "CREATE_COACH" &&
            item.label !== "USERS" &&
            item.label !== "AREA_ACTIONS" &&
            item.label !== "REPORT_CATEGORIES",
        );
      }
    }
    this.menu = menu.concat(this.municipalityHomeURL);
  }

  public ngOnDestroy(): void {
    this.unsubscribe("routeChanges");
  }

  setLoading(load: boolean): void {
    setTimeout(() => {
      if (load) {
        // Increment loading & set loading true
        this.totalLoading++;
        if (this.loading == false) {
          this.loading = true;
        }
      } else {
        // Decrement loading & set loading based on amount of things loading
        this.totalLoading--;
        this.loading = this.totalLoading > 0;
      }
      // Emit to subject
      this.loadingSubject.next(this.loading);
    }, 0);
  }
}
